import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild, TemplateRef, inject } from '@angular/core';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { SetupScreensService } from '../../services/setup-screens.service';
import { Store } from '@ngxs/store';
import {
  PlantPosts,
  PlantProcessCode,
  PlantSettings,
  PlantSetup,
  PlantWorkTypes,
  TdmsProperties,
  TimeZone,
  WorkFlowDetails
} from '../../models/plant-setup.model';
import { FormBuilder, FormGroup, FormArray, FormControl, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { EGRA_POSTS } from '../../models/plant-posts-model';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatTableDataSource } from '@angular/material/table';
import { TableComponent, TableConfig, AlertService, AlertType, Alert, ModalComponent, RowItem, ModalResult } from '@michelin/theme';
import { DealerState } from '../../../../shared/state/DealerState';
import { hasRolePermission, getCountries, mapCountry } from '../../../user-management/utils/UserSetupUtils';
import { ROLES } from '../../../authentication/authRoles.constants';
import { Router } from '@angular/router';
import { CanComponentDeactivate } from '../../../../shared/guards/unsaved-changes.guard';
import { canDeactivateForm } from '../../../../shared/utility/canDeactivateForm';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-plant-setup-details',
  templateUrl: './plant-setup-details.component.html',
  styleUrls: ['./plant-setup-details.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlantSetupDetailsComponent implements OnInit, CanComponentDeactivate {
  @ViewChild('tableRef', { static: false }) tableRef: TableComponent<TdmsProperties>;
  @ViewChild('deleteContent') deleteContent: TemplateRef<any>;
  @ViewChild('deleteConfirmation', { static: true }) deleteModal: ModalComponent;
  @ViewChild('unsavedChangesModal', { static: true }) unsavedChangesModal: ModalComponent;
  @ViewChild('unsavedConfirmation') unsavedChangesContentModal: TemplateRef<any>;

  public translationService = inject(TranslationService);
  public setupScreensService = inject(SetupScreensService);
  public featureFlagsService = inject(FeatureflagsService);
  public fb = inject(FormBuilder);
  public cdr = inject(ChangeDetectorRef);
  public store = inject(Store);
  public alertService = inject(AlertService);
  public router = inject(Router);

  plantDetails: PlantSetup = null;
  plantSettings: PlantSettings = null;
  zoneSettings: PlantSettings = null;
  tdmsProperties = new MatTableDataSource<TdmsProperties>();
  tdmsPropertiesReady = false;
  productSaveDisabled = true;
  saveEnabled = false;
  isLoading = false;
  plantSetupDetails: FormGroup;
  plantPosts: FormArray;
  plantProcessCode: FormArray;
  plantWorkTypes: FormArray;
  workFlowDetails: FormArray;
  plantForm: FormGroup;
  tdmsPropertiesForm: FormGroup;
  timeZones: TimeZone[];
  timeZoneLabels = [];
  postDropdowns = ['Y', 'N'];
  plantTypes = ['M3', 'M4', 'MRT', 'Oliver', 'Recamic'];
  countries = getCountries();
  ynFields = [
    'plantActive',
    'reqTdInitial',
    'ckCamSpecWo',
    'ckCamSpecWoShopfloor',
    'receivedDtEditable',
    'receivedDtPreviousDay',
    'reqBarcodeEntry',
    'sndRawMaterial',
    'scrapShipping',
    'captureXRayRes',
    'autoIncBarcode',
    'disblRjctLblDis',
    'disblRjctLblRet',
    'allowPlantPlantTransfer',
    'allowDueDateWe',
    'allowMiscCharges',
    'manageInventory',
    'forceRejectByCasing',
    'rememberFieldsOnAddLine',
    'includeScrapCasingsRejectCasing',
    'enableCsdReport',
    'exportCsdToFile',
    'rfidEnabledFlag',
    'rfidAutoWrite',
    'rfidAutoReadInitial',
    'filterBiasSizes',
    'automaticRemainingTreadDepth',
    'printTreadPrep',
    'maxSpeedIndexAllowedPlant',
    'engravingIntegration',
    'captureCasingSpeedIndex'
  ];
  protected readonly environment = environment;

  tableConfig: TableConfig<TdmsProperties> = {
    options: {
      maxHeight: '200px',
      scrollOptions: { options: { scrollbars: { autoHide: 'never' } } }
    },
    columns: {
      plantCode: {
        cell: (item: TdmsProperties) => item.plantCode,
        header: this.translationService.translate('plantCodeText')
      },
      postNbr: {
        cell: (item: TdmsProperties) => item.postNbr,
        isEditable: true,
        header: this.translationService.translate('postNumberText')
      },
      dbUrl: {
        cell: (item: TdmsProperties) => item.dbUrl,
        isEditable: true,
        header: this.translationService.translate('dbUrlText')
      },
      dbSchema: {
        cell: (item: TdmsProperties) => item.dbSchema,
        isEditable: true,
        header: this.translationService.translate('schemaText')
      },
      dbUser: {
        cell: (item: TdmsProperties) => item.dbUser,
        isEditable: true,
        header: this.translationService.translate('dbUserText')
      },
      dbPassword: {
        cell: (item: TdmsProperties) => item.dbPassword,
        isEditable: true,
        header: this.translationService.translate('passwordEncryptedText')
      },
      tableName: {
        cell: (item: TdmsProperties) => item.tableName,
        isEditable: true,
        header: this.translationService.translate('tableNameText')
      },
      byPassTreadDepth: {
        cell: (item: TdmsProperties) => item.byPassTreadDepth,
        isEditable: true,
        header: this.translationService.translate('byPassTreadDepthText')
      },
      encoding: {
        cell: (item: TdmsProperties) => item.encoding,
        header: this.translationService.translate('encodingText')
      },
      action: { header: this.translationService.translate('actionText'), cell: 'action' }
    }
  };

  workflowItemsRR: string[] = [];
  workflowItemsRO: string[] = [];
  workflowItemsRS: string[] = [];
  postNames = ['Post 1', 'Post 2', 'Post 3', 'Post 4', 'Post 5', 'Post 6', 'Post 7', 'Post 8', 'Post 9', 'Post 10'];

  originalPlantPosts: PlantPosts[] = [];
  // Keep a baseline of last committed values to compute diffs on edits
  private readonly lastCommittedTdmsRows = new Map<string, TdmsProperties>();
  // true when navigated via /plantSetup/create (no existing plant in store)
  isCreateMode = false;
  existingPlants: PlantSetup[] = [];

  private itemToDelete: TdmsProperties | null = null;

  constructor() {}

  ngOnInit(): void {
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    this.saveEnabled = !this.productSaveDisabled && hasRolePermission(ROLES.edit_product);
    if (this.productSaveDisabled) {
      this.alertService.showAlert({
        type: AlertType.classical,
        title: this.translationService.translate('readOnlyProductDataHeader'),
        content: this.translationService.translate('readOnlyProductDataText'),
        timestamp: 60000
      } as Alert);
    }

    this.initPlantSetupDetailsForm();
    this.plantPosts = this.fb.array([]);
    this.plantProcessCode = this.fb.array([]);
    this.plantWorkTypes = this.fb.array([]);
    this.workFlowDetails = this.fb.array([]);
    this.tdmsPropertiesForm = this.fb.group({
      tdmsId: [''],
      postNbr: [''],
      plantCode: [''],
      encoding: [''],
      dbUrl: [''],
      dbUser: [''],
      dbPassword: [''],
      dbSchema: [''],
      tableName: [''],
      byPassTreadDepth: ['']
    });
    this.plantForm = this.fb.group({
      plantSetupDetails: this.plantSetupDetails,
      plantPosts: this.plantPosts,
      plantProcessCode: this.plantProcessCode,
      plantWorkTypes: this.plantWorkTypes,
      workFlowDetails: this.workFlowDetails
    });

    // Load plant details
    this.plantDetails = this.store.selectSnapshot(DealerState.selectedPlant);
    // Determine mode: create when there is no existing plant id in store
    this.isCreateMode = !this.plantDetails?.id;

    if (this.isCreateMode) {
      // Create mode defaults: active flag on, today's install date, MRT plant type
      this.plantSetupDetails.patchValue(
        {
          plantActive: true,
          installDt: new Date(),
          plantType: 'MRT'
        },
        { emitEvent: false }
      );
    }

    if (this.isCreateMode) {
      // Load existing plants for duplicate code/name validation
      this.setupScreensService.getAllPlants().subscribe({
        next: (response) => {
          this.existingPlants = response.data || [];
          // Add unique validators now that we have the plant data
          this.plantSetupDetails.get('code').addValidators(this.uniqueCodeValidator.bind(this));
          this.plantSetupDetails.get('name').addValidators(this.uniqueNameValidator.bind(this));
          this.plantSetupDetails.get('code').updateValueAndValidity();
          this.plantSetupDetails.get('name').updateValueAndValidity();
          this.cdr.markForCheck();
        }
      });
    }
    if (!this.isCreateMode) {
      // Edit mode: patch form with existing plant data
      const patchObj = this.convertYNFieldsToBoolean(this.plantDetails, this.ynFields);
      const woCompleteStartTime = new Date(this.plantDetails.woCompleteStartTime);
      this.plantSetupDetails.patchValue({ ...patchObj, woCompleteStartTime }, { emitEvent: true });
    }

    if (this.isCreateMode) {
      // CREATE MODE: load zone settings only
      this.setupScreensService.getZoneSetting().subscribe({
        next: (response) => {
          this.plantSettings = response.data.plantSettings;
          this.zoneSettings = response.data.zoneSettings;
          this.timeZones = response.data.timeZones;
          this.timeZoneLabels = this.timeZones.map((tz) => tz.timezoneLabel);
          this.tdmsPropertiesReady = true;
          this.workFlowControlsCache.clear();
          const postsArray = this.createPlantPostsFormArray(this.zoneSettings?.plantPosts || []);
          const procCodeArray = this.createPlantProcessCodeFormArray(this.zoneSettings?.plantProcessCodes || []);
          const workTypesArray = this.createPlantWorkTypesFormArray(this.zoneSettings?.plantWorkTypes || []);
          const workflowArray = this.createWorkFlowDetailsFormArray(this.zoneSettings?.workFlowDetails || []);
          this.plantForm.setControl('plantPosts', postsArray);
          this.plantForm.setControl('plantProcessCode', procCodeArray);
          this.plantForm.setControl('plantWorkTypes', workTypesArray);
          this.plantForm.setControl('workFlowDetails', workflowArray);
          this.plantPosts = postsArray;
          this.plantProcessCode = procCodeArray;
          this.plantWorkTypes = workTypesArray;
          this.workFlowDetails = workflowArray;
          this.workflowItemsRR = this.getWorkFlowControls('RR').map((ctrl) => ctrl.get('postName').value || '');
          this.workflowItemsRO = this.getWorkFlowControls('RO').map((ctrl) => ctrl.get('postName').value || '');
          this.workflowItemsRS = this.getWorkFlowControls('RS').map((ctrl) => ctrl.get('postName').value || '');
          this.setupShippingReceivingSync();
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading zone settings:', error);
          this.alertService.showAlert({
            type: AlertType.strong,
            content: this.translationService.translate('tryAgainText')
          } as Alert);
          this.tdmsPropertiesReady = true;
          this.cdr.markForCheck();
        }
      });
    } else {
      // EDIT MODE: load plant + zone settings
      this.setupScreensService.getPlantAndZoneSetting(this.plantDetails?.id).subscribe({
        next: (response) => {
          this.plantSettings = response.data.plantSettings;
          this.zoneSettings = response.data.zoneSettings;
          this.timeZones = response.data.timeZones;
          this.timeZoneLabels = this.timeZones.map((tz) => tz.timezoneLabel);
          this.tdmsProperties = new MatTableDataSource(response.data.tdmsProperties ?? []);
          this.tdmsPropertiesReady = true;
          this.cdr.markForCheck();
          if (response.data.tdmsProperties) {
            this.lastCommittedTdmsRows.clear();
            response.data.tdmsProperties.forEach((r: TdmsProperties) => this.lastCommittedTdmsRows.set(r.tdmsId, { ...r }));
            this.tdmsPropertiesForm.patchValue(response.data.tdmsProperties, { emitEvent: false });
          }
          this.plantSetupDetails.patchValue(
            {
              timezoneFk: this.timeZones.find((tz) => tz.timezonePk === this.plantDetails.timezoneFk)?.timezoneLabel,
              countryCode: mapCountry(this.plantDetails.countryCode)
            },
            { emitEvent: false }
          );
          this.workFlowControlsCache.clear();
          const postsArray = this.createPlantPostsFormArray(this.plantSettings?.plantPosts || []);
          const procCodeArray = this.createPlantProcessCodeFormArray(this.plantSettings?.plantProcessCodes || []);
          const workTypesArray = this.createPlantWorkTypesFormArray(this.plantSettings?.plantWorkTypes || []);
          const workflowArray = this.createWorkFlowDetailsFormArray(this.plantSettings?.workFlowDetails || []);
          this.plantForm.setControl('plantPosts', postsArray);
          this.plantForm.setControl('plantProcessCode', procCodeArray);
          this.plantForm.setControl('plantWorkTypes', workTypesArray);
          this.plantForm.setControl('workFlowDetails', workflowArray);
          this.plantPosts = postsArray;
          this.plantProcessCode = procCodeArray;
          this.plantWorkTypes = workTypesArray;
          this.workFlowDetails = workflowArray;
          this.cdr.markForCheck();
          this.workflowItemsRR = this.getWorkFlowControls('RR').map((ctrl) => ctrl.get('postName').value || '');
          this.workflowItemsRO = this.getWorkFlowControls('RO').map((ctrl) => ctrl.get('postName').value || '');
          this.workflowItemsRS = this.getWorkFlowControls('RS').map((ctrl) => ctrl.get('postName').value || '');
          const availablePostNames = new Set(Object.keys(EGRA_POSTS).filter((key) => EGRA_POSTS[key].availableForPlantPosts));
          this.originalPlantPosts = (this.plantSettings?.plantPosts || [])
            .filter((post) => availablePostNames.has(post.postName) && !this.isDisabled('plantPosts', post.postName))
            .map((post) => ({ ...post }));
          this.setupShippingReceivingSync();
        },
        error: (error) => {
          console.error('Error loading plant and zone settings:', error);
          this.alertService.showAlert({
            type: AlertType.strong,
            content: this.translationService.translate('tryAgainText')
          } as Alert);
          this.tdmsPropertiesReady = true;
          this.cdr.markForCheck();
        }
      });
    }
  }

  private initPlantSetupDetailsForm(): void {
    this.plantSetupDetails = this.fb.group({
      id: [''],
      name: ['', Validators.required],
      code: ['', Validators.required],
      dealerFk: [''],
      addressOne: [''],
      addressTwo: [''],
      city: [''],
      stateProvince: [''],
      postalCode: [''],
      countryCode: [''],
      contactName: [''],
      phoneNumber: ['', PlantSetupDetailsComponent.internationalPhoneValidator],
      faxNumber: [''],
      emailAdress: ['', Validators.email],
      plantActive: [false],
      plantNumber: ['', [Validators.required, Validators.pattern(/^\d{1,2}$/)]],
      installDt: ['', Validators.required],
      reqTdInitial: [false],
      ckCamSpecWo: [false],
      ckCamSpecWoShopfloor: [false],
      dueDtElapsedTime: [null, Validators.required],
      receivedDtEditable: [false],
      receivedDtPreviousDay: [false],
      reqBarcodeEntry: [false],
      sndRawMaterial: [false],
      sndRawMaterialTimeLimit: [null],
      interfaceId1: [''],
      interfaceId2: [''],
      shipping: ['N'],
      receiving: ['N'],
      scrapShipping: [false],
      captureXRayRes: [false],
      autoIncBarcode: [false],
      disblRjctLblDis: [false],
      disblRjctLblRet: [false],
      allowPlantPlantTransfer: [false],
      allowDueDateWe: [false],
      allowMiscCharges: [false],
      timeDelayToCompletion: [null],
      manageInventory: [false],
      plantType: ['', Validators.required],
      forceRejectByCasing: [false],
      woCompleteStartTime: ['', Validators.required],
      michelinShipToNumber: ['', [Validators.required, Validators.pattern(/^\d{1,10}$/)]],
      rememberFieldsOnAddLine: [false],
      includeScrapCasingsRejectCasing: [false],
      enableCsdReport: [false],
      exportCsdToFile: [false],
      exportCsdFolder: [''],
      timezoneFk: ['', Validators.required],
      rfidEnabledFlag: [false],
      rfidTimer: [null],
      rfidAutoWrite: [false],
      rfidReadTimerFinal: [null],
      rfidWriteTimerFinal: [null],
      rfidLockTimerFinal: [null],
      rfidWriteAttemptFinal: [null],
      rfidLockAttemptFinal: [null],
      rfidAutoReadInitial: [false],
      filterBiasSizes: [false],
      automaticRemainingTreadDepth: [false],
      lastUpdtTimestamp: [''],
      printTreadPrep: [false],
      engravingIntegration: [false],
      captureCasingSpeedIndex: [false],
      maxSpeedIndexAllowedPlant: [false]
    });
  }

  private createPlantPostsFormArray(posts: PlantPosts[]): FormArray {
    const availablePostNames = Object.keys(EGRA_POSTS).filter((key) => EGRA_POSTS[key].availableForPlantPosts);
    availablePostNames.sort((a, b) => EGRA_POSTS[a].labelKey.localeCompare(EGRA_POSTS[b].labelKey));

    let groups: ReturnType<typeof this.fb.group>[];
    if (this.isCreateMode) {
      // Create mode: all EGRA posts not disabled in zone, pre-populated from zone settings
      groups = availablePostNames
        .filter((name) => !this.isDisabled('plantPosts', name))
        .map((name) => {
          const zonePost = posts.find((post) => post.postName === name);
          const postActive = zonePost?.postActive || 'Y';
          return this.fb.group({
            plantPostsPk: [''],
            plantFk: [''],
            postName: [name],
            postActive: [{ value: postActive, disabled: postActive === 'R' }]
          });
        });
    } else {
      // Edit mode: posts saved for this plant, filtered by zone settings
      groups = availablePostNames
        .map((name) => posts.find((post) => post.postName === name))
        .filter((post) => !!post && !this.isDisabled('plantPosts', post.postName))
        .map((post) =>
          this.fb.group({
            plantPostsPk: [post.plantPostsPk || ''],
            plantFk: [post.plantFk || ''],
            postName: [post.postName || ''],
            postActive: [{ value: post.postActive || '', disabled: post.postActive === 'R' }]
          })
        );
    }

    // Add hidden SHIPPING and RECEIVING posts (synced via shipping/receiving form controls)
    const hiddenPostNames = ['SHIPPING', 'RECEIVING'];
    for (const name of hiddenPostNames) {
      const existingPost = posts.find((post) => post.postName === name);
      const shippingReceivingControlName = name === 'SHIPPING' ? 'shipping' : 'receiving';
      const controlValue = this.plantSetupDetails.get(shippingReceivingControlName)?.value;
      // Determine postActive: use the current form control value if available, else fall back to existing post data
      const postActive = controlValue || existingPost?.postActive || 'N';
      groups.push(
        this.fb.group({
          plantPostsPk: [existingPost?.plantPostsPk || ''],
          plantFk: [existingPost?.plantFk || ''],
          postName: [name],
          postActive: [postActive]
        })
      );
    }

    return this.fb.array(groups);
  }

  /**
   * Subscribes to shipping/receiving form control changes and syncs
   * the corresponding SHIPPING/RECEIVING plant post's postActive value.
   */
  private setupShippingReceivingSync(): void {
    const syncPostActive = (postName: string, value: string) => {
      const postControl = this.plantPosts.controls.find((ctrl) => ctrl.get('postName')?.value === postName);
      if (postControl) {
        postControl.get('postActive')?.setValue(value, { emitEvent: false });
        this.plantForm.markAsDirty();
      }
    };

    this.plantSetupDetails.get('shipping')?.valueChanges.subscribe((value: string) => {
      syncPostActive('SHIPPING', value);
    });

    this.plantSetupDetails.get('receiving')?.valueChanges.subscribe((value: string) => {
      syncPostActive('RECEIVING', value);
    });
  }

  private createPlantProcessCodeFormArray(codes: PlantProcessCode[]): FormArray {
    return this.fb.array(
      codes.map((code) =>
        this.fb.group({
          plantProcessCodePk: [code.plantProcessCodePk || ''],
          plantFk: [code.plantFk || ''],
          processCode: [code.processCode || ''],
          processCodeDescription: [code.processCodeDescription || ''],
          active: [code.active || '']
        })
      )
    );
  }

  private createPlantWorkTypesFormArray(types: PlantWorkTypes[]): FormArray {
    return this.fb.array(
      types.map((type) =>
        this.fb.group({
          plantFk: [type.plantFk || ''],
          workType: [type.workType || ''],
          workTypeDescription: [type.workTypeDescription || ''],
          active: [type.active || '']
        })
      )
    );
  }

  private createWorkFlowDetailsFormArray(details: WorkFlowDetails[]): FormArray {
    return this.fb.array(
      details.map((detail) =>
        this.fb.group({
          workFlowDetailsPk: [detail.workFlowDetailsPk || ''],
          plantFk: [detail.plantFk || ''],
          workType: [detail.workType || ''],
          workFlowOrder: [detail.workFlowOrder ?? null],
          postName: [detail.postName || '']
        })
      )
    );
  }

  private ynToBoolean(value: string): boolean {
    return value === 'Y';
  }

  private convertYNFieldsToBoolean(obj: any, keys: string[]): any {
    const result = { ...obj };
    keys.forEach((key) => {
      result[key] = this.ynToBoolean(obj[key]);
    });
    return result;
  }

  getProcessCodeControl(code: string, controlName: string): FormControl | null {
    const idx = this.plantProcessCode?.controls?.findIndex((ctrl) => ctrl.get('processCode').value === code);
    if (idx !== -1 && idx != null) {
      const control = this.plantProcessCode.at(idx).get(controlName) as FormControl;
      if (control?.value === 'R') {
        control.disable();
      } else {
        control.enable();
      }
      return control;
    }
    return null;
  }

  // Cache for workflow controls to prevent infinite loops
  private readonly workFlowControlsCache = new Map<string, FormGroup[]>();

  getWorkFlowControls(code: string): FormGroup[] {
    // Check if we already have cached controls for this code
    if (this.workFlowControlsCache.has(code)) {
      return this.workFlowControlsCache.get(code);
    }

    // Get existing controls for this work type and sort them
    const existingControls = this.workFlowDetails?.controls
      ? this.workFlowDetails.controls
          .filter((ctrl) => (ctrl as FormGroup).get('workType')?.value === code)
          .sort((a, b) => ((a as FormGroup).get('workFlowOrder')?.value ?? 0) - ((b as FormGroup).get('workFlowOrder')?.value ?? 0))
          .map((ctrl) => ctrl as FormGroup)
      : [];
    // Cache the result
    this.workFlowControlsCache.set(code, existingControls);

    return existingControls;
  }

  getPostLabel(postName: string): string {
    return EGRA_POSTS[postName]?.labelKey || postName;
  }

  isHiddenPost(postName: string): boolean {
    return postName === 'SHIPPING' || postName === 'RECEIVING';
  }

  trackByIndex(index: number): number {
    return index;
  }
  onNumericInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '');
    this.plantSetupDetails.get('dueDtElapsedTime')?.setValue(input.value, { emitEvent: false });
  }

  getWorkflowPostNames(workType: string): string[] {
    // Collect all postNames currently in workFlowDetails for the given workType
    const usedPostNames =
      this.workFlowDetails?.controls?.filter((ctrl) => ctrl.get('workType').value === workType).map((ctrl) => ctrl.get('postName').value) ||
      [];
    return Object.keys(EGRA_POSTS)
      .filter((key) => EGRA_POSTS[key].availableForWorkflow && !usedPostNames.includes(key))
      .sort((a, b) => EGRA_POSTS[a].labelKey.localeCompare(EGRA_POSTS[b].labelKey));
  }

  getWorkflowItems(workType: string): string[] {
    if (workType === 'RR') return this.workflowItemsRR;
    if (workType === 'RO') return this.workflowItemsRO;
    if (workType === 'RS') return this.workflowItemsRS;
    return [];
  }

  drop(event: CdkDragDrop<string[]>, workType: string) {
    const items = this.getWorkflowItems(workType);
    moveItemInArray(items, event.previousIndex, event.currentIndex);
    const workflowControls = this.getWorkFlowControls(workType);
    workflowControls.forEach((ctrl, idx) => {
      ctrl.get('postName').setValue(items[idx]);
      ctrl.get('workFlowOrder').setValue(idx + 1);
    });
    // Clear cache to ensure fresh+ data
    this.workFlowControlsCache.delete(workType);
    this.plantForm.markAsDirty();
    this.cdr.markForCheck();
  }

  onAddPost(postName: string, workType: string) {
    if (!postName) return;

    // Check if the post is marked as 'N' (inactive) in the posts section
    const postStatus = this.plantPosts.getRawValue().find((p) => p.postName === postName)?.postActive;
    if (postStatus === 'N') {
      this.alertService.showAlert({
        type: AlertType.strong,
        content:
          this.translationService.translate('invalidWorkflowPostsText') +
          ': ' +
          this.translationService.translate(this.getPostLabel(postName))
      } as Alert);
      return;
    }

    // Prevent duplicate posts in workflow for the same workType
    const items = this.getWorkflowItems(workType);
    if (items.includes(postName)) {
      return;
    }
    items.push(postName);
    const workflowControls = this.getWorkFlowControls(workType);
    const newControl = this.fb.group({
      workFlowDetailsPk: [''],
      plantFk: [this.plantDetails?.id || ''],
      workType: [workType],
      workFlowOrder: [workflowControls.length + 1], // Start from 1, not 0
      postName: [postName]
    });
    this.workFlowDetails.push(newControl);
    // Clear cache to ensure fresh data
    this.workFlowControlsCache.delete(workType);
    this.plantForm.markAsDirty();
    this.cdr.markForCheck();
  }

  onDeleteWorkflowItem(index: number, workType: string) {
    const items = this.getWorkflowItems(workType);
    items.splice(index, 1);
    const workflowControls = this.getWorkFlowControls(workType);
    if (workflowControls?.[index]) {
      this.workFlowDetails.removeAt(this.workFlowDetails.controls.indexOf(workflowControls[index]));
    }
    // Clear cache before getting fresh controls
    this.workFlowControlsCache.delete(workType);
    this.getWorkFlowControls(workType).forEach((ctrl, idx) => {
      ctrl.get('workFlowOrder').setValue(idx + 1); // 1-based order
    });
    this.plantForm.markAsDirty();
    this.cdr.markForCheck();
  }

  // Convert boolean values to 'Y'/'N' format expected by backend
  private booleanToYN(value: boolean): string {
    return value ? 'Y' : 'N';
  }

  // Converts form data with boolean values to Y/N format for backend
  private convertFormDataToYN(formData: any): any {
    const result = { ...formData };

    this.ynFields.forEach((field) => {
      if (typeof result[field] === 'boolean') {
        result[field] = this.booleanToYN(result[field]);
      }
    });

    // Remove RFID fields if rfidEnabledFlag is not 'Y'
    if (result.rfidEnabledFlag !== 'Y') {
      delete result.rfidTimer;
      delete result.rfidAutoWrite;
      delete result.rfidReadTimerFinal;
      delete result.rfidWriteTimerFinal;
      delete result.rfidLockTimerFinal;
      delete result.rfidWriteAttemptFinal;
      delete result.rfidLockAttemptFinal;
      delete result.rfidAutoReadInitial;
    }

    return result;
  }

  save() {
    if (!this.plantSetupDetails.valid) {
      this.plantSetupDetails.markAllAsTouched();
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('fillAllMandatoryFieldText')
      } as Alert);
      return;
    }
    const stateProvince = this.plantSetupDetails.get('stateProvince')?.value;
    if (stateProvince?.trim() && !this.isValidState(stateProvince)) {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('invalidStateProvinceText')
      } as Alert);
      return;
    }

    if (this.isCreateMode) {
      this.saveEnabled = false;
      this.isLoading = true;
      this.cdr.markForCheck();

      try {
        const plantSetupDetailsRaw = this.plantSetupDetails.getRawValue();
        const plantSetupDetailsFormatted = this.convertFormDataToYN(plantSetupDetailsRaw);
        if (plantSetupDetailsFormatted.timezoneFk && this.timeZones?.length) {
          const selectedTimezone = this.timeZones.find((tz) => tz.timezoneLabel === plantSetupDetailsFormatted.timezoneFk);
          if (selectedTimezone) {
            plantSetupDetailsFormatted.timezoneFk = selectedTimezone.timezonePk;
          }
        }
        if (plantSetupDetailsFormatted.rfidEnabledFlag !== 'Y') {
          delete plantSetupDetailsFormatted.rfidTimer;
          delete plantSetupDetailsFormatted.rfidAutoWrite;
          delete plantSetupDetailsFormatted.rfidReadTimerFinal;
          delete plantSetupDetailsFormatted.rfidWriteTimerFinal;
          delete plantSetupDetailsFormatted.rfidLockTimerFinal;
          delete plantSetupDetailsFormatted.rfidWriteAttemptFinal;
          delete plantSetupDetailsFormatted.rfidLockAttemptFinal;
          delete plantSetupDetailsFormatted.rfidAutoReadInitial;
        }

        const currentPosts = this.plantPosts.getRawValue().map((post) => ({
          ...post,
          postActive: typeof post.postActive === 'boolean' ? this.booleanToYN(post.postActive) : post.postActive
        }));

        const payload: any = {
          ...plantSetupDetailsFormatted,
          plantPosts: currentPosts,
          plantProcessCodes: this.plantProcessCode.getRawValue(),
          workFlowDetails: this.workFlowDetails.getRawValue()
        };
        delete payload.id;

        this.setupScreensService.createPlant(payload).subscribe({
          next: () => {
            this.alertService.showAlert({
              type: AlertType.success,
              content: this.translationService.translate('plantSetupChangesText')
            } as Alert);
            this.isLoading = false;
            this.saveEnabled = true;
            this.plantForm.markAsPristine();
            this.cdr.markForCheck();
            this.router.navigate(['/home/setup/plantSetup']);
          },
          error: () => {
            this.alertService.showAlert({
              type: AlertType.strong,
              content: this.translationService.translate('tryAgainText')
            } as Alert);
            this.isLoading = false;
            this.saveEnabled = true;
            this.cdr.markForCheck();
          }
        });
      } catch {
        this.alertService.showAlert({
          type: AlertType.strong,
          content: this.translationService.translate('tryAgainText')
        } as Alert);
        this.isLoading = false;
        this.saveEnabled = true;
        this.cdr.markForCheck();
      }
    } else {
      // Edit mode
      const isSetupDetailsDirty = this.plantSetupDetails.dirty;
      const currentPosts = this.plantPosts.getRawValue().map((post) => ({
        ...post,
        postActive: typeof post.postActive === 'boolean' ? this.booleanToYN(post.postActive) : post.postActive
      }));
      const isPostsChanged =
        currentPosts.length !== this.originalPlantPosts.length ||
        currentPosts.some((current) => {
          const original = this.originalPlantPosts.find((orig) => orig.postName === current.postName);
          if (!original) return true;
          return current.postActive !== original.postActive;
        });

      const currentProcessCodes = this.plantProcessCode.getRawValue();
      const originalProcessCodes = this.plantSettings?.plantProcessCodes || [];
      const isProcessCodeChanged =
        currentProcessCodes.length !== originalProcessCodes.length ||
        currentProcessCodes.some((current, idx) => {
          const original = originalProcessCodes[idx];
          if (!original) return true;
          return Object.keys(current).some((key) => current[key] !== original[key]);
        });

      const currentWorkflowDetails = this.workFlowDetails.getRawValue();
      const originalWorkflowDetails = this.plantSettings?.workFlowDetails || [];
      const isWorkflowDetailsChanged =
        currentWorkflowDetails.length !== originalWorkflowDetails.length ||
        currentWorkflowDetails.some((current, idx) => {
          const original = originalWorkflowDetails[idx];
          if (!original) return true;
          return Object.keys(current).some((key) => current[key] !== original[key]);
        });

      if (!isSetupDetailsDirty && !isPostsChanged && !isProcessCodeChanged && !isWorkflowDetailsChanged) {
        this.alertService.showAlert({
          type: AlertType.strong,
          content: this.translationService.translate('noModifiedText')
        } as Alert);
        return;
      }
      if (!this.plantSetupDetails.valid) {
        this.plantSetupDetails.markAllAsTouched();
        this.alertService.showAlert({
          type: AlertType.strong,
          content: this.translationService.translate('fillAllMandatoryFieldText')
        } as Alert);
        return;
      }

      // Validate workflow posts
      const postStatusMap = new Map<string, string>();
      this.plantPosts.getRawValue().forEach((post) => {
        postStatusMap.set(post.postName, post.postActive);
      });

      const invalidWorkflowPosts: { postName: string; workType: string }[] = [];
      this.workFlowDetails.getRawValue().forEach((workflow) => {
        const postStatus = postStatusMap.get(workflow.postName);
        if (postStatus === 'N') {
          invalidWorkflowPosts.push({
            postName: workflow.postName,
            workType: workflow.workType
          });
        }
      });

      if (invalidWorkflowPosts.length > 0) {
        const postLabels = invalidWorkflowPosts
          .map((item) => `${this.translationService.translate(this.getPostLabel(item.postName))} (${item.workType})`)
          .join(', ');
        this.alertService.showAlert({
          type: AlertType.strong,
          content: this.translationService.translate('invalidWorkflowPostsText') + ': ' + postLabels
        } as Alert);
        return;
      }

      this.saveEnabled = false;
      this.isLoading = true;
      this.cdr.markForCheck();

      try {
        const payload: any = { id: this.plantDetails?.id };
        const plantSetupDetailsRaw = this.plantSetupDetails.getRawValue();
        if (this.plantDetails?.woCompleteStartTime && plantSetupDetailsRaw?.woCompleteStartTime) {
          const original = this.plantDetails.woCompleteStartTime;
          const datePart = original.substring(0, 10);
          const timeDate: Date =
            plantSetupDetailsRaw.woCompleteStartTime instanceof Date
              ? plantSetupDetailsRaw.woCompleteStartTime
              : new Date(plantSetupDetailsRaw.woCompleteStartTime);
          const hh = String(timeDate.getHours()).padStart(2, '0');
          const mm = String(timeDate.getMinutes()).padStart(2, '0');
          plantSetupDetailsRaw.woCompleteStartTime = `${datePart} ${hh}:${mm}:00.0`;
        }
        const plantSetupDetailsFormatted = this.convertFormDataToYN(plantSetupDetailsRaw);
        if (plantSetupDetailsFormatted.timezoneFk && this.timeZones?.length) {
          const selectedTimezone = this.timeZones.find((tz) => tz.timezoneLabel === plantSetupDetailsFormatted.timezoneFk);
          if (selectedTimezone) {
            plantSetupDetailsFormatted.timezoneFk = selectedTimezone.timezonePk;
          }
        }
        if (plantSetupDetailsFormatted.rfidEnabledFlag !== 'Y') {
          delete plantSetupDetailsFormatted.rfidTimer;
          delete plantSetupDetailsFormatted.rfidAutoWrite;
          delete plantSetupDetailsFormatted.rfidReadTimerFinal;
          delete plantSetupDetailsFormatted.rfidWriteTimerFinal;
          delete plantSetupDetailsFormatted.rfidLockTimerFinal;
          delete plantSetupDetailsFormatted.rfidWriteAttemptFinal;
          delete plantSetupDetailsFormatted.rfidLockAttemptFinal;
          delete plantSetupDetailsFormatted.rfidAutoReadInitial;
        }
        Object.assign(payload, plantSetupDetailsFormatted);

        payload.plantPosts = currentPosts;
        payload.plantProcessCodes = this.plantProcessCode.getRawValue();
        payload.workFlowDetails = this.workFlowDetails.getRawValue();

        this.setupScreensService.updatePlant(payload).subscribe({
          next: () => {
            this.alertService.showAlert({
              type: AlertType.success,
              content: this.translationService.translate('plantSetupChangesText')
            } as Alert);
            this.isLoading = false;
            this.saveEnabled = true;
            this.plantForm.markAsPristine();
            this.cdr.markForCheck();
          },
          error: () => {
            this.alertService.showAlert({
              type: AlertType.strong,
              content: this.translationService.translate('tryAgainText')
            } as Alert);
            this.isLoading = false;
            this.saveEnabled = true;
            this.cdr.markForCheck();
          }
        });
      } catch {
        this.alertService.showAlert({
          type: AlertType.strong,
          content: this.translationService.translate('tryAgainText')
        } as Alert);
        this.isLoading = false;
        this.saveEnabled = true;
        this.cdr.markForCheck();
      }
    }
  }

  cancel(): void {
    this.router.navigate(['/home/setup/plantSetup']);
  }
  /**
   * Adds a new TDMS property row to the table and enters edit mode
   */
  onAddNewRule(): void {
    // Check if any row is currently being edited
    const rowEditData = (this.tableRef as any)?.rowEditData;
    if (rowEditData?.data) {
      const hasEditingRow = rowEditData.data.some((item: any) => item.isEditing === true);
      if (hasEditingRow) {
        // Show alert to inform user they must save or cancel current edit first
        this.alertService.showAlert({
          type: AlertType.strong,
          content: this.translationService.translate('finishEditingRowText')
        } as Alert);
        return; // Exit early without adding a new row
      }
    }

    // Store current table data for potential rollback
    const currentData = this.tdmsProperties.data;

    // Request a new unique TDMS ID from the backend
    this.setupScreensService.getNewTdmsId().subscribe({
      next: (response: any) => {
        const newId = response?.data;

        // Create a new row with default values populated from plant details
        const newRow: TdmsProperties = {
          tdmsId: newId,
          postNbr: null,
          plantCode: this.plantDetails?.code ?? null,
          encoding: 'ISO-8859-1',
          dbUrl: null,
          dbUser: null,
          dbPassword: null,
          dbSchema: null,
          tableName: null,
          byPassTreadDepth: null,
          dealerFk: this.plantDetails?.dealerFk ?? null,
          plantSetupFk: this.plantDetails?.id ?? null
        };

        // Prepend new row to the beginning of the table data
        this.tdmsProperties.data = [newRow, ...currentData];

        // Track this row as uncommitted (empty baseline for diffing)
        this.lastCommittedTdmsRows.set(String(newId), {} as TdmsProperties);

        // Update the edit data store with the new row in edit mode
        const rowEditData = (this.tableRef as any)?.rowEditData;
        if (rowEditData) {
          rowEditData.data = this.tdmsProperties.data.map((item) => ({
            ...item,
            isEditing: item.tdmsId === newRow.tdmsId, // Only new row is editable
            isValid: true
          }));
        }

        // Trigger Angular change detection
        this.cdr.markForCheck();

        // Asynchronously activate edit mode and focus the first input field
        queueMicrotask(() => {
          const firstRow = this.tdmsProperties.data[0];
          if (this.tableRef && firstRow) {
            // Enable row editing mode for the new row
            this.tableRef.rowEditMode(firstRow);
            // Focus the Post Number input after a short delay to ensure DOM is ready
            setTimeout(() => this.focusPostNbrInFirstRow(), 150);
          }
        });
      },
      error: () => {
        // Show critical error alert if ID generation fails
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('criticalErrorAlertText')
        } as Alert);
      }
    });
  }

  /**
   * Extracts the edited TDMS row from a table edit event
   * @param evt - The event object emitted by the table component
   * @returns An object containing the extracted row data and its unique key (tdmsId)
   */
  private extractEditedRowFromEvent(evt: any): { row: TdmsProperties | null; key?: string } {
    // Check multiple nested paths where the row data might exist
    const candidates = [evt, evt?.item, evt?.row, evt?.data, evt?.row?.item, evt?.item?.item, evt?.data?.item];

    // Loop through all possible locations to find a valid TDMS row
    for (const candidate of candidates) {
      if (this.isValidTdmsRow(candidate)) {
        // Return the first valid row found along with its unique identifier
        return { row: candidate, key: candidate.tdmsId ?? candidate.id };
      }
    }

    // If no valid row found, extract just the key from various possible locations
    return { row: null, key: evt?.key ?? evt?.id ?? evt?.uniqueKey };
  }

  /**
   * Validates whether a candidate object is a valid TDMS properties row
   * @param candidate - The object to validate
   * @returns true if the candidate contains at least one required TDMS field
   */
  private isValidTdmsRow(candidate: any): boolean {
    // Reject null, undefined, or non-object values
    if (!candidate || typeof candidate !== 'object') return false;

    // List of all expected fields in a TDMS properties row
    const requiredKeys = ['tdmsId', 'plantCode', 'dbUrl', 'dbUser', 'dbSchema', 'tableName', 'encoding', 'postNbr'];

    // Row is valid if it contains at least one of these fields
    // (some() returns true if at least one key exists in the candidate)
    return requiredKeys.some((key) => key in candidate);
  }

  /**
   * Builds a complete TDMS row by merging event changes with existing data
   * @param event - The table edit event containing row changes
   * @returns An object with the resolved tdmsId and the complete merged row
   */
  private buildFullRowFromEvent(event: RowItem<TdmsProperties>): { tdmsId: string | null; row: TdmsProperties | null } {
    const { tdmsId, changes } = this.extractChangesFromEvent(event);

    const baseRow = this.findBaseRow(tdmsId);

    const fullRow = this.mergeRowData(tdmsId, baseRow, changes);

    return { tdmsId, row: fullRow };
  }

  /**
   * Extracts the tdmsId and changed field values from a table edit event
   * @param event - The table edit event
   * @returns Object containing the tdmsId and a map of changed fields
   */
  private extractChangesFromEvent(event: any): { tdmsId: string | null; changes: { [key: string]: any } } {
    if (event?.value && Array.isArray(event.value) && (typeof event.key === 'string' || typeof event.key === 'number')) {
      const tdmsId = String(event.key);
      const changes = event.value.reduce((acc, { column, newValue }) => ({ ...acc, [column]: newValue }), {});
      return { tdmsId, changes };
    }
    // Delegate to helper method to handle multiple possible nesting structures
    const { row, key } = this.extractEditedRowFromEvent(event);
    const tdmsId = row?.tdmsId ?? (key ? String(key) : null);
    // Use the entire row object as changes (will be merged with defaults later)
    const changes = row || {};

    return { tdmsId, changes };
  }

  /**
   * Finds an existing TDMS row by its unique identifier
   * @param tdmsId - The unique identifier of the row to find
   * @returns The found row or null if not found in either location
   */
  private findBaseRow(tdmsId: string | null): TdmsProperties | null {
    if (!tdmsId) return null;

    // Helper function to search in a data array by tdmsId
    const findById = (data: TdmsProperties[] | null) => data?.find((row) => String(row?.tdmsId) === String(tdmsId)) || null;

    // Search main data first, fallback to edit buffer if not found
    return findById(this.tdmsProperties?.data) || findById((this.tableRef as any)?.rowEditData?.data);
  }

  /**
   * Merges default values, existing row data, and new changes into a complete TDMS row
   * @param tdmsId - The unique identifier for the row
   * @param baseRow - The existing row data (null for new rows)
   * @param changes - The field changes from the edit event
   * @returns The complete merged row or null if no changes provided
   */
  private mergeRowData(tdmsId: string | null, baseRow: TdmsProperties | null, changes: { [key: string]: any }): TdmsProperties | null {
    // Define default values for all TDMS properties
    // Populated from plant details where available
    const defaults: TdmsProperties = {
      tdmsId: tdmsId ?? null,
      postNbr: null,
      plantCode: this.plantDetails?.code ?? null,
      encoding: 'ISO-8859-1',
      dbUrl: null,
      dbUser: null,
      dbPassword: null,
      dbSchema: null,
      tableName: null,
      byPassTreadDepth: null,
      dealerFk: this.plantDetails?.dealerFk ?? null,
      plantSetupFk: this.plantDetails?.id ?? null
    };

    // For existing rows: merge all three layers
    if (baseRow) {
      return { ...defaults, ...baseRow, ...changes };
    }

    // For new rows: only merge defaults with changes (skip empty changes)
    return Object.keys(changes).length ? { ...defaults, ...changes } : null;
  }

  /**
   * Main handler for table row value changes
   * @param tdmsProperties - The row edit event from the table component
   */
  rowValueChanged(tdmsProperties: RowItem<TdmsProperties>): void {
    const { tdmsId: resolvedId, row: fullRow } = this.buildFullRowFromEvent(tdmsProperties);
    if (!fullRow) {
      console.warn('Could not resolve full row from event', tdmsProperties);
      return;
    }

    const tdmsId = String(fullRow.tdmsId ?? resolvedId);

    const updatedRow = this.updateLocalData(tdmsId, fullRow);

    const payload = this.buildPayload(tdmsId, updatedRow);

    this.saveRowChanges(tdmsId, updatedRow, payload);
  }

  /**
   * Updates the local table data source with the changed row (optimistic update)
   * @param tdmsId - The unique identifier of the row being updated
   * @param fullRow - The complete row data with all changes
   * @returns The final merged row that was added/updated in the table
   */
  private updateLocalData(tdmsId: string, fullRow: TdmsProperties): TdmsProperties {
    // Find the row in the current data source
    const idx = this.tdmsProperties.data.findIndex((r) => String(r.tdmsId) === tdmsId);

    // Merge existing row data with new changes (or use fullRow if it's new)
    const updatedRow = idx >= 0 ? { ...this.tdmsProperties.data[idx], ...fullRow } : fullRow;

    // Create a new array to trigger Angular change detection
    const data = [...this.tdmsProperties.data];
    if (idx >= 0) {
      // Update existing row at its current position
      data[idx] = updatedRow;
    } else {
      // Add new row to the beginning of the table
      data.unshift(updatedRow);
    }

    // Update the MatTableDataSource and force re-render
    this.tdmsProperties.data = data;
    this.tdmsProperties._updateChangeSubscription(); // Notify MatTableDataSource
    this.tableRef?.table?.renderRows?.(); // Force Material table to re-render

    // Update the baseline for future change tracking
    const baseline = this.lastCommittedTdmsRows.get(tdmsId) || {};
    this.lastCommittedTdmsRows.set(tdmsId, { ...baseline, ...updatedRow });

    return updatedRow;
  }

  /**
   * Builds the API payload for saving TDMS property changes
   * @param tdmsId - The unique identifier of the row
   * @param updatedRow - The complete row data with all changes
   * @returns The payload object ready for the API call
   */
  private buildPayload(tdmsId: string, updatedRow: TdmsProperties): TdmsProperties {
    return {
      ...updatedRow,
      tdmsId,
      // Ensure dealerFk is set (use existing value or fallback to plant details)
      dealerFk: updatedRow.dealerFk ?? this.plantDetails?.dealerFk,
      // Always use current plant's ID
      plantSetupFk: this.plantDetails?.id
    };
  }

  /**
   * Sends the TDMS property changes to the server
   *
   * Makes an HTTP request to persist the changes and handles success/error responses.
   *
   * @param tdmsId - The unique identifier of the row being saved
   * @param updatedRow - The complete row data (for UI updates on success)
   * @param payload - The API payload to send to the server
   */
  private saveRowChanges(tdmsId: string, updatedRow: TdmsProperties, payload: TdmsProperties): void {
    const normalizedValue = this.normalizeByPassTreadDepth(payload.byPassTreadDepth);

    if (normalizedValue && normalizedValue !== 'NULL') {
      payload.byPassTreadDepth = updatedRow.byPassTreadDepth = normalizedValue;
    }

    const validation = this.validateTdmsRow(payload);
    if (!validation.isValid) {
      this.alertService.showAlert({
        type: AlertType.critical,
        title: this.translationService.translate('validationErrorText'),
        content: validation.message,
        timestamp: 5000
      } as Alert);

      const failedRow = this.tdmsProperties.data.find((item) => String(item.tdmsId) === tdmsId);
      if (failedRow) {
        Object.assign(failedRow, updatedRow);

        setTimeout(() => {
          this.tableRef?.rowEditMode(failedRow);
          const tableComponent = this.tableRef as any;
          tableComponent?.rowEditData?.data?.forEach((item: any) => {
            item.isEditing = String(item.tdmsId) === tdmsId;
            item.isValid = true;
          });

          this.focusInvalidField(tdmsId, validation.message);
          this.cdr.markForCheck();
        }, 50);
      }
      return;
    }

    this.setupScreensService.updateTdmsProperties(payload).subscribe({
      next: () => this.handleSaveSuccess(tdmsId, updatedRow),
      error: (err) => this.handleSaveError(err, payload)
    });
  }

  /**
   * Focuses on the invalid field based on validation error message
   * @param tdmsId - The row's unique identifier
   * @param errorMessage - The validation error message
   */
  private focusInvalidField(tdmsId: string, errorMessage: string): void {
    setTimeout(() => {
      // Find the row element for this specific tdmsId
      const rowIndex = this.tdmsProperties.data.findIndex((r) => String(r.tdmsId) === tdmsId);
      const rows = document.querySelectorAll('mic-table .mat-mdc-table .mat-mdc-row');
      const targetRow = rows[rowIndex] as HTMLElement;

      if (!targetRow) return;

      let inputField: HTMLInputElement | null = null;

      // Determine which field to focus based on error message (case-insensitive)
      const lowerErrorMsg = errorMessage.toLowerCase();

      if (lowerErrorMsg.includes('post number') || lowerErrorMsg.includes('postnbr')) {
        inputField = targetRow.querySelector('.mat-column-postNbr input');
      } else if (lowerErrorMsg.includes('bypass') || lowerErrorMsg.includes('tread depth')) {
        inputField = targetRow.querySelector('.mat-column-byPassTreadDepth input');
      }

      // Focus and select the invalid input field
      if (inputField) {
        inputField.focus();
        inputField.select();
      }
    }, 150);
  }

  /**
   * Handles successful server save by synchronizing all data stores and UI
   * @param tdmsId - The unique identifier of the saved row
   * @param updatedRow - The complete row data that was successfully saved
   */
  private handleSaveSuccess(tdmsId: string, updatedRow: TdmsProperties): void {
    const idx = this.tdmsProperties.data.findIndex((r) => String(r.tdmsId) === tdmsId);
    if (idx !== -1) {
      this.tdmsProperties.data[idx] = { ...this.tdmsProperties.data[idx], ...updatedRow };
      this.tdmsProperties._updateChangeSubscription(); // Notify MatTableDataSource
      this.tableRef?.table?.renderRows?.(); // Force table re-render
    }

    const editData = this.tableRef?.rowEditData?.data;
    if (Array.isArray(editData)) {
      const editIdx = editData.findIndex((r: any) => String(r.tdmsId) === tdmsId);
      if (editIdx !== -1) {
        // Mark row as no longer being edited
        editData[editIdx] = { ...editData[editIdx], ...updatedRow, isEditing: false };
        this.tableRef.rowEditData.data = [...editData]; // Trigger change detection
      }
    }

    const baseline = this.lastCommittedTdmsRows.get(tdmsId) || {};
    this.lastCommittedTdmsRows.set(tdmsId, { ...baseline, ...updatedRow });

    this.cdr.markForCheck();
    this.alertService.showAlert({
      type: AlertType.success,
      content: this.translationService.translate('camSaveSuccessText')
    } as Alert);
  }

  /**
   * Handles server save errors by logging and showing error message to user
   *
   * @param err - The error object from the failed HTTP request
   * @param payload - The payload that failed to save (for debugging)
   */
  private handleSaveError(err: any, payload: TdmsProperties): void {
    // Log error details for debugging
    console.error('Failed to update TdmsProperties', err, payload);

    // Show user-friendly error alert
    this.alertService.showAlert({
      type: AlertType.strong,
      title: this.translationService.translate('errorText'),
      content: this.translationService.translate('tryAgainText'),
      timestamp: 2000
    });
  }

  /**
   * Opens a confirmation modal before deleting a TDMS property row
   * @param item - The TDMS property row to be deleted
   */
  showDeleteModal(item: TdmsProperties): void {
    // Open Material modal with confirmation buttons
    this.deleteModal.open({
      id: 'deleteConfirmation',
      title: this.translationService.translate('warningText'),
      width: 550,
      content: this.deleteContent, // TemplateRef for modal body
      confirm: this.translationService.translate('yesText'),
      cancel: this.translationService.translate('noText')
    });

    // Store the item to delete (will be used in onDeleteModalClosed)
    this.itemToDelete = item;
  }

  /**
   * Handles the delete confirmation modal result
   * @param result - The modal result object containing user's choice
   */
  onDeleteModalClosed(result: ModalResult): void {
    // User cancelled or closed modal - reset state
    if (!result?.result || result.id !== 'deleteConfirmation') {
      this.itemToDelete = null;
      return;
    }

    // User confirmed deletion - proceed
    const row = this.itemToDelete;
    this.itemToDelete = null; // Clear the pending delete
    if (!row) return;

    const tdmsId = String(row.tdmsId);

    // Check if this is a new row that was never saved to the server
    const isLocalOnly = this.isNewUnsavedRow(tdmsId);

    if (isLocalOnly) {
      // Row only exists in UI - remove locally without server call
      this.removeRowLocally(tdmsId);
      this.showSuccessAlert();
      return;
    }

    // Row exists on server - send DELETE request
    this.deleteRowFromServer(tdmsId);
  }

  /**
   * Checks if a row is a new, unsaved row (exists only in the UI)
   *
   * A row is considered "unsaved" if it has no baseline entry or the baseline is empty.
   * The baseline tracks what was last successfully saved to the server.
   *
   * @param tdmsId - The unique identifier of the row to check
   * @returns true if the row was never saved to the server
   */
  private isNewUnsavedRow(tdmsId: string): boolean {
    const baseline = this.lastCommittedTdmsRows.get(tdmsId);
    // No baseline = never saved, or baseline with no keys = empty save
    return !baseline || !Object.keys(baseline).length;
  }

  /**
   * Removes a TDMS row from all local data stores without server interaction
   * @param tdmsId - The unique identifier of the row to remove
   */
  private removeRowLocally(tdmsId: string): void {
    this.tdmsProperties.data = this.tdmsProperties.data.filter((r) => String(r.tdmsId) !== tdmsId);

    const editStore = (this.tableRef as any)?.rowEditData?.data;
    if (Array.isArray(editStore)) {
      (this.tableRef as any).rowEditData.data = editStore.filter((r: any) => String(r.tdmsId) !== tdmsId);
    }

    this.lastCommittedTdmsRows.delete(tdmsId);

    this.tdmsProperties._updateChangeSubscription?.(); // Notify MatTableDataSource
    (this.tableRef as any)?.table?.renderRows?.(); // Force table re-render
    this.cdr.markForCheck(); // Trigger Angular change detection
  }

  /**
   * Sends a DELETE request to the server and removes the row from UI on success
   * @param tdmsId - The unique identifier of the row to delete from the server
   */
  private deleteRowFromServer(tdmsId: string): void {
    this.setupScreensService.deleteTdmsProperties(tdmsId).subscribe({
      next: () => {
        // Server deletion successful - remove from UI
        this.removeRowLocally(tdmsId);
        this.showSuccessAlert();
      },
      error: () => {
        // Server deletion failed - show error (keep row in UI)
        this.showErrorAlert();
      }
    });
  }

  /**
   * Shows a success alert message to the user
   */
  private showSuccessAlert(): void {
    this.alertService.showAlert({
      type: AlertType.success,
      content: this.translationService.translate('deletedText')
    } as Alert);
  }

  /**
   * Shows an error alert message to the user
   */
  private showErrorAlert(): void {
    this.alertService.showAlert({
      type: AlertType.strong,
      title: this.translationService.translate('errorText'),
      content: this.translationService.translate('tryAgainText'),
      timestamp: 2000
    });
  }

  /**
   * Focuses the postNbr input field in the first row of the TDMS properties table
   * @param attempt - Current retry attempt number (max 10 retries with 50ms intervals)
   * Retry strategy: Ensures DOM is ready before attempting to focus
   */
  private focusPostNbrInFirstRow(attempt = 0): void {
    // Uses specific Material Design table selectors for the Michelin table component
    const root = document.querySelector('mic-table .mat-mdc-table .mat-mdc-row:first-child');

    if (!root) {
      // Retry up to 10 times (500ms total) to wait for Angular to render the table
      if (attempt < 10) setTimeout(() => this.focusPostNbrInFirstRow(attempt + 1), 50);
      return;
    }

    // Checks multiple possible input types: input, textarea, contenteditable, mat-select
    const target: HTMLElement = root.querySelector('.mat-column-postNbr input, textarea, [contenteditable="true"], mat-select');

    if (target) {
      target.focus(); // Give keyboard focus to the input

      // For text inputs, move cursor to end of existing text
      const input = target as HTMLInputElement;
      input.setSelectionRange?.(input.value.length, input.value.length);
    } else if (attempt < 10) {
      // If input not found but row exists, retry (input might still be rendering)
      setTimeout(() => this.focusPostNbrInFirstRow(attempt + 1), 50);
    }
  }

  /**
   * Validates a TDMS property row before saving
   * @param row - The TDMS property row to validate
   * @returns Object with isValid flag and error message (if any)
   */
  private validateTdmsRow(row: TdmsProperties): { isValid: boolean; message?: string } {
    // Required fields validation
    const requiredFields = [
      { field: 'postNbr', label: 'Post Number' },
      { field: 'dbUrl', label: 'DB URL' },
      { field: 'dbSchema', label: 'Schema' },
      { field: 'dbUser', label: 'DB User' },
      { field: 'dbPassword', label: 'Password' },
      { field: 'tableName', label: 'Table Name' },
      { field: 'byPassTreadDepth', label: 'ByPassTread' }
    ];

    // Check for missing required fields
    for (const { field, label } of requiredFields) {
      if (!row[field] || String(row[field]).trim() === '') {
        return {
          isValid: false,
          message: this.translationService.translate('fieldIsRequiredText').replace('{0}', label)
        };
      }
    }

    // Validate Post Number (postNbr)
    const postNbrValidation = this.validatePostNumber(row.postNbr, row.tdmsId);
    if (!postNbrValidation.isValid) {
      return postNbrValidation;
    }

    // Validate ByPassTreadDepth
    const byPassValidation = this.validateByPassTreadDepth(row.byPassTreadDepth);
    if (!byPassValidation.isValid) {
      return byPassValidation;
    }

    return { isValid: true };
  }

  /**
   * Validates the Post Number field
   * @returns Validation result with error message if invalid
   */
  private validatePostNumber(postNbr: any, currentTdmsId: string): { isValid: boolean; message?: string } {
    const postNbrStr = String(postNbr).trim();

    // Check if numeric
    if (!/^\d+$/.test(postNbrStr)) {
      return {
        isValid: false,
        message: this.translationService.translate('postNumberMustBeNumericText')
      };
    }

    const postNbrNum = Number.parseInt(postNbrStr, 10);

    // Check range (1-999)
    if (postNbrNum < 1 || postNbrNum > 99) {
      return {
        isValid: false,
        message: this.translationService.translate('postNumberRangeText')
      };
    }

    // Check for duplicates in the same plant
    const isDuplicate = this.tdmsProperties.data.some(
      (row) =>
        String(row.tdmsId) !== String(currentTdmsId) && String(row.postNbr) === postNbrStr && row.plantCode === this.plantDetails?.code
    );

    if (isDuplicate) {
      return {
        isValid: false,
        message: this.translationService.translate('duplicatePostNumberText')
      };
    }

    return { isValid: true };
  }

  /**
   * Validates the ByPassTreadDepth field
   *
   * @param byPassTreadDepth - The value to validate
   * @returns Validation result with error message if invalid
   */
  private validateByPassTreadDepth(byPassTreadDepth: any): { isValid: boolean; message?: string } {
    if (!byPassTreadDepth) {
      return {
        isValid: false,
        message: this.translationService.translate('byPassTreadRequiredText')
      };
    }

    const normalized = String(byPassTreadDepth).trim().toUpperCase();

    if (normalized !== 'Y' && normalized !== 'N') {
      return {
        isValid: false,
        message: this.translationService.translate('byPassTreadValueText')
      };
    }

    return { isValid: true };
  }

  /**
   * Normalizes ByPassTreadDepth value to uppercase Y/N
   */
  private normalizeByPassTreadDepth(value: any): string {
    const str = String(value).trim().toLowerCase();
    if (str === 'y') return 'Y';
    if (str === 'n') return 'N';
    return String(value).trim().toUpperCase();
  }

  private isValidPhoneNumber(phone: string): boolean {
    // International phone number validation (E.164 compatible)
    // Accepts: +1234567890, +1 (234) 567-890, 00 1 234 567 890, etc.
    // Must have 7-15 digits total (ITU-T E.164 standard)
    const cleaned = phone.replace(/[^\d]/g, '');
    if (cleaned.length < 7 || cleaned.length > 15) return false;
    return /^\+?[\d\s\-().]+$/.test(phone.trim());
  }

  static internationalPhoneValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value || !value.trim()) return null; // optional field
    const cleaned = value.replace(/[^\d]/g, '');
    if (cleaned.length < 7 || cleaned.length > 15) {
      return { invalidPhone: true };
    }
    if (!/^\+?[\d\s\-().]+$/.test(value.trim())) {
      return { invalidPhone: true };
    }
    return null;
  }

  private uniqueCodeValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value || !this.existingPlants?.length) return null;
    const isDuplicate = this.existingPlants.some((plant) => plant.code?.toLowerCase() === control.value.trim().toLowerCase());
    return isDuplicate ? { duplicateCode: true } : null;
  }

  private uniqueNameValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value || !this.existingPlants?.length) return null;
    const isDuplicate = this.existingPlants.some((plant) => plant.name?.toLowerCase() === control.value.trim().toLowerCase());
    return isDuplicate ? { duplicateName: true } : null;
  }

  private isValidState(state: string): boolean {
    // Accepts 2-letter codes or alphabetic state/province names (min 2 chars)
    return /^[A-Za-z\s-]{2,}$/.test(state.trim());
  }

  /** Returns true when the plant form has unsaved changes. */
  isFormChanged(): boolean {
    return this.plantForm?.dirty ?? false;
  }

  /**
   * Determines if a field or post should be disabled based on zone settings.
   * @param section The section name, e.g., 'plantPosts' or 'plantProcessCodes'
   * @param postName The post name or process code to look up in zone settings
   * @returns true if the post/code is missing from zone settings or marked inactive (N)
   */
  isDisabled(section: string, postName: string): boolean {
    if (!this.zoneSettings) return true;
    if (section === 'plantPosts') {
      const posts = this.zoneSettings.plantPosts;
      if (!posts) return true;
      const post = posts.find((p) => p.postName === postName);
      if (!post) return true;
      return post.postActive === 'N';
    }
    if (section === 'plantProcessCodes') {
      const codes = this.zoneSettings.plantProcessCodes;
      if (!codes) return true;
      const code = codes.find((c) => c.processCode === postName);
      if (!code) return true;
      return code.active === 'N';
    }
    return false;
  }

  canDeactivate(): Promise<boolean> | boolean {
    if (!this.isFormChanged()) return true;
    if (!this.unsavedChangesModal || !this.unsavedChangesContentModal) return false;
    return canDeactivateForm(
      () => this.isFormChanged(),
      this.unsavedChangesModal,
      this.unsavedChangesContentModal,
      this.translationService
    );
  }
}
